import { H3LocalBadge } from "./H3LocalBadge";
import { LtxLocalBadge } from "./LtxLocalBadge";
import { EchoLanguageBadge } from "./EchoLanguageBadge";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Aperture,
  ArrowRight,
  Building2,
  Check,
  Copy,
  Download,
  ExternalLink,
  Film,
  Gauge,
  Image as ImageIcon,
  Layers,
  Mic,
  Music,
  Palette,
  Play,
  Quote,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Star,
  Type,
  User,
  Video,
  ZoomIn,
} from "lucide-react";

import type {
  EchoQcFinding,
  EchoflicksSessionMedia,
  EchoflicksSessionMessage,
  EchoflicksVideoSession,
  I2VPromptAuditSummary,
} from "../types";
import { EmptyHint } from "./EchoflicksSessionList";
import {
  formatDateTime,
  isAudio,
  isImage,
  isVideo,
  sessionStatusLabel,
  statusBadgeVariant,
} from "./sessionUtils";
import { useMediaUrl } from "./useMediaUrl";
import { useDisplayableImage } from "./useDisplayableImage";
import {
  aspectLabel,
  buildStoryboard,
  directorScriptOf,
  findTitleScene,
  formatSeconds,
  sceneKindLabel,
  summarizeCut,
  summarizeMusic,
  summarizeTitleDecision,
  summarizeVoice,
  type EchoCutSummary,
  type SceneI2VAudit,
  type StoryScene,
} from "./scriptStory";
import { buildI2VPairs, type I2VPair } from "./i2vShowcase";
import { MediaGalleryDialog } from "./MediaGalleryDialog";
import { galleryCaption, isGalleryVideo } from "./mediaGalleryUtils";
import {
  parallaxMoveLabelFromPrompt,
  promptIsParallax,
} from "./sceneBreakdown";
import {
  orientationFromDimensions,
  qcAgentLabel,
  qcVerdictLabel,
  summarizeQc,
  type Orientation,
  type QcSummary,
} from "./qcSummary";
import { normalizeSessionMessage } from "../whatsapp/chat";
import { EchoAttributionBadges } from "./EchoAttributionBadges";
import { EchoCreditsBadge } from "./EchoCreditsBadge";
import { LazyEchoShareStatus } from "./LazyEchoShareStatus";
import { WebEchoBadge } from "./WebEchoBadge";
import { resolveEchoAttribution, resolveEchoRecurrence } from "./attribution";
import {
  isStudioFilm,
  studioAgencyLabel,
  studioAgencyName,
  studioCreator,
  studioCreatorLabel,
} from "./studioFilms";
import { useEchoContact } from "./useEchoContact";
import { useEchoReferral } from "./useEchoReferral";
import { useStudioDirectory } from "./useStudioDirectory";
import { DisplayCropBadge } from "./DisplayCropBadge";
import { occasionBadgeLabel } from "./occasion";

type MediaUrlResult = ReturnType<typeof useMediaUrl>;
type Chip = { icon: ReactNode; label: string; title?: string };
type EnvelopeInput = Record<string, unknown> | undefined;

const STORYBOARD_PREVIEW_COUNT = 6;

// ---------------------------------------------------------------------------
// Header: who/what/when for the Echo, above the Director View.
//
// The finished film itself plays in the Director View (the page's first real
// surface); this strip carries the title, the badges and the record-level
// actions (Contact, Download) so the film is never rendered twice on one page.
// `children` land in the action cluster — the page puts its admin Debug switch
// there.
// ---------------------------------------------------------------------------

export const EchoHeader = ({
  record,
  finalVideo,
  isWebEcho,
  isAdmin = false,
  children,
}: {
  record: EchoflicksVideoSession;
  /** Signed final-video URL, for the Download action only. */
  finalVideo: MediaUrlResult;
  /**
   * Made on my.echoflicks.com (MYC-090). The page passes what it learned from
   * the session's events too; without it the ECHO row marker decides.
   */
  isWebEcho?: boolean;
  /**
   * CRM administrator (server-verified identity): unlocks staff moderation
   * such as "Disable link" on the share badge (ESH-063).
   */
  isAdmin?: boolean;
  children?: ReactNode;
}) => {
  const title = record.movie_title ?? record.script_title ?? "Untitled echo";
  const { contact, isLoading: contactLoading } = useEchoContact(record);
  const { referral, isLoading: referralLoading } = useEchoReferral(record);
  // B6.3: a Studio film shows its agency (and creator) instead of the
  // phantom `studio-…` contact; the names come from the admin-only directory.
  const studioFilm = isStudioFilm(record);
  const directory = useStudioDirectory({ enabled: studioFilm });
  const attribution = resolveEchoAttribution(record, contact, referral, {
    agencyName: studioAgencyName(record, directory.agenciesById),
  });
  const recurrence = useMemo(
    () => resolveEchoRecurrence(record, contact),
    [record, contact],
  );
  const occasionLabel = occasionBadgeLabel(
    record.occasion_key,
    typeof record.raw_metadata?.category === "string"
      ? record.raw_metadata.category
      : null,
  );
  const creator = studioFilm
    ? studioCreator(record, directory.loginsById)
    : null;
  const meta: (Chip | null)[] = [
    studioFilm
      ? {
          icon: <Building2 className="size-3.5" />,
          label: studioAgencyLabel(record, directory.agenciesById),
        }
      : record.contact_name || record.whatsapp_user_id
        ? {
            icon: <User className="size-3.5" />,
            label: record.contact_name ?? record.whatsapp_user_id,
          }
        : null,
    creator?.id
      ? {
          icon: <User className="size-3.5" />,
          label: `${studioCreatorLabel(creator)}${creator.isStaff ? " (staff)" : ""}`,
        }
      : null,
    record.uploaded_media_count
      ? {
          icon: <ImageIcon className="size-3.5" />,
          label: `${record.uploaded_media_count} photo${
            record.uploaded_media_count === 1 ? "" : "s"
          }`,
        }
      : null,
    record.completed_at
      ? {
          icon: <Sparkles className="size-3.5" />,
          label: formatDateTime(record.completed_at),
        }
      : null,
  ];
  const metaChips = meta.filter((item): item is Chip => Boolean(item));

  return (
    <Card data-testid="echo-header">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <h2 className="text-xl font-semibold leading-tight">{title}</h2>
            {record.operator_synopsis ? (
              <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
                {record.operator_synopsis}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={statusBadgeVariant(record.status)}>
                {sessionStatusLabel(record.status)}
              </Badge>
              {occasionLabel ? (
                <Badge variant="outline">{occasionLabel}</Badge>
              ) : null}
              <WebEchoBadge record={record} isWeb={isWebEcho} />
              <H3LocalBadge summary={record.i2v_provider_summary} />
              <LtxLocalBadge summary={record.i2v_provider_summary} />
              <EchoLanguageBadge record={record} />
              <StarRow rating={record.rating} />
              <EchoAttributionBadges
                attribution={attribution}
                recurrence={recurrence}
                contactLoading={contactLoading}
                referralLoading={referralLoading}
              />
              <EchoCreditsBadge record={record} />
              <LazyEchoShareStatus record={record} isAdmin={isAdmin} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {metaChips.map((item) => (
                <span
                  key={item.label}
                  className="inline-flex items-center gap-1.5"
                >
                  {item.icon}
                  {item.label}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {children}
            {record.contact_id ? (
              <Button asChild variant="outline" size="sm">
                <Link to={`/contacts/${record.contact_id}/show`}>
                  Contact
                  <ExternalLink className="size-4" />
                </Link>
              </Button>
            ) : null}
            {finalVideo.url ? (
              <Button asChild size="sm">
                <a href={finalVideo.url} target="_blank" rel="noreferrer">
                  <Download className="size-4" />
                  Download
                </a>
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StarRow = ({ rating }: { rating?: number | null }) => {
  if (!rating) {
    return <span className="text-sm text-muted-foreground">Not yet rated</span>;
  }
  const rounded = Math.round(rating);
  return (
    <span
      className="inline-flex items-center gap-0.5 text-amber-500"
      aria-label={`Rated ${rating} of 5`}
    >
      {[1, 2, 3, 4, 5].map((index) => (
        <Star
          key={index}
          className={`size-4 ${
            index <= rounded ? "fill-current" : "text-muted-foreground/40"
          }`}
        />
      ))}
      <span className="ml-1 text-sm font-medium text-foreground">
        {rating}/5
      </span>
    </span>
  );
};

// ---------------------------------------------------------------------------
// The brief: what the person asked for
// ---------------------------------------------------------------------------

export const EchoBrief = ({
  record,
  media,
  messages,
}: {
  record: EchoflicksVideoSession;
  media: EchoflicksSessionMedia[];
  messages: EchoflicksSessionMessage[];
}) => {
  const uploads = useMemo(
    () => media.filter((item) => item.kind === "uploaded_media"),
    [media],
  );
  const galleryMedia = useMemo(
    () =>
      uploads.filter((item) => isImage(item.mime_type) || isGalleryVideo(item)),
    [uploads],
  );
  const uploadedPhotos = useMemo(
    () => uploads.filter((item) => isImage(item.mime_type)),
    [uploads],
  );
  const [activeGalleryIndex, setActiveGalleryIndex] = useState<number | null>(
    null,
  );
  const requestText = useMemo(() => {
    for (const message of messages) {
      const chat = normalizeSessionMessage(message, record.contact_name);
      if (chat.sender === "contact" && chat.text && !chat.isCommand) {
        return chat.text;
      }
    }
    return null;
  }, [messages, record.contact_name]);

  const [clientOrient, setClientOrient] = useState<Record<string, Orientation>>(
    {},
  );

  useEffect(() => {
    if (
      activeGalleryIndex !== null &&
      activeGalleryIndex >= galleryMedia.length
    ) {
      setActiveGalleryIndex(null);
    }
  }, [activeGalleryIndex, galleryMedia.length]);

  if (uploads.length === 0 && !requestText) return null;

  const orientationOf = (photo: EchoflicksSessionMedia): Orientation | null =>
    (photo.orientation as Orientation | undefined) ??
    orientationFromDimensions(photo.width, photo.height) ??
    clientOrient[String(photo.id)] ??
    null;

  const portraitCount = uploadedPhotos.filter(
    (p) => orientationOf(p) === "portrait",
  ).length;

  return (
    <Card id="echo-native-sources">
      <CardHeader>
        <CardTitle>The brief</CardTitle>
        <p className="text-sm text-muted-foreground">
          What {record.contact_name?.split(" ")[0] ?? "the sender"} sent in.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {requestText ? (
          <div className="flex items-start gap-2 rounded-md bg-muted/60 p-3 text-sm">
            <Quote className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <p className="min-w-0">{requestText}</p>
          </div>
        ) : null}
        {uploads.length > 0 ? (
          <>
            {portraitCount > 0 ? (
              <div className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">
                  {portraitCount} of {uploadedPhotos.length}
                </span>{" "}
                {portraitCount === 1 ? "photo is" : "photos are"} portrait.
              </div>
            ) : null}
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
              {uploads.map((item) => {
                const galleryIndex = galleryMedia.findIndex(
                  (galleryItem) => galleryItem.id === item.id,
                );
                return (
                  <BriefThumb
                    key={item.id}
                    media={item}
                    orientation={orientationOf(item)}
                    galleryPosition={
                      galleryIndex >= 0
                        ? {
                            index: galleryIndex,
                            total: galleryMedia.length,
                          }
                        : null
                    }
                    onOpen={
                      galleryIndex >= 0
                        ? () => setActiveGalleryIndex(galleryIndex)
                        : undefined
                    }
                    onOrientation={(o) =>
                      setClientOrient((prev) =>
                        prev[String(item.id)] === o
                          ? prev
                          : { ...prev, [String(item.id)]: o },
                      )
                    }
                  />
                );
              })}
            </div>
          </>
        ) : null}
      </CardContent>
      <MediaGalleryDialog
        items={galleryMedia}
        activeIndex={activeGalleryIndex}
        onActiveIndexChange={setActiveGalleryIndex}
      />
    </Card>
  );
};

const BriefThumb = ({
  media,
  orientation,
  galleryPosition,
  onOpen,
  onOrientation,
}: {
  media: EchoflicksSessionMedia;
  orientation: Orientation | null;
  galleryPosition: { index: number; total: number } | null;
  onOpen?: () => void;
  onOrientation: (o: Orientation) => void;
}) => {
  const mediaUrl = useDisplayableImage(media);
  const video = isGalleryVideo(media);
  const [videoPreviewFailed, setVideoPreviewFailed] = useState(false);
  const caption = galleryCaption(media);

  return (
    <div className="space-y-1">
      <div className="relative">
        {mediaUrl.url && (isImage(media.mime_type) || video) ? (
          <button
            type="button"
            onClick={onOpen}
            className="group block w-full cursor-pointer overflow-hidden rounded-md bg-black text-left outline-none ring-offset-2 transition focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-background"
            aria-label={
              galleryPosition
                ? `Open ${video ? "video" : "photo"} ${galleryPosition.index + 1} of ${galleryPosition.total} in gallery`
                : `Open ${video ? "video" : "photo"} in gallery`
            }
          >
            {video ? (
              <>
                {videoPreviewFailed ? (
                  <span className="flex aspect-square w-full items-center justify-center bg-muted text-muted-foreground">
                    <Video className="size-6" aria-hidden />
                  </span>
                ) : (
                  <video
                    data-testid={`brief-video-thumbnail-${media.id}`}
                    src={videoThumbnailSource(mediaUrl.url)}
                    aria-hidden="true"
                    muted
                    playsInline
                    preload="metadata"
                    className="aspect-square w-full object-cover transition duration-200 group-hover:scale-[1.02] group-hover:brightness-75"
                    onError={() => setVideoPreviewFailed(true)}
                    onLoadedMetadata={(event) => {
                      const derived = orientationFromDimensions(
                        event.currentTarget.videoWidth,
                        event.currentTarget.videoHeight,
                      );
                      if (derived) onOrientation(derived);
                    }}
                  />
                )}
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-white">
                  <span className="rounded-full bg-black/65 p-3 shadow-lg transition group-hover:scale-110">
                    <Play className="size-5 fill-current" aria-hidden />
                  </span>
                </span>
              </>
            ) : (
              <>
                <img
                  src={mediaUrl.url}
                  alt={caption ?? media.s3_key ?? "Uploaded photo"}
                  title={caption ?? undefined}
                  className="aspect-square w-full object-cover transition duration-200 group-hover:scale-[1.02] group-hover:brightness-90"
                  loading="lazy"
                  onError={mediaUrl.refresh}
                  onLoad={(event) => {
                    const derived = orientationFromDimensions(
                      event.currentTarget.naturalWidth,
                      event.currentTarget.naturalHeight,
                    );
                    if (derived) onOrientation(derived);
                  }}
                />
                <span className="pointer-events-none absolute right-1.5 top-1.5 rounded-full bg-black/65 p-1.5 text-white opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
                  <ZoomIn className="size-4" aria-hidden />
                </span>
              </>
            )}
            <DisplayCropBadge label="Square display crop" />
          </button>
        ) : (
          <div className="flex aspect-square w-full items-center justify-center rounded-md bg-muted text-muted-foreground">
            <ThumbIcon media={media} />
          </div>
        )}
        {orientation ? (
          <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[11px] font-medium capitalize text-white">
            {orientation}
          </span>
        ) : null}
      </div>
      {caption ? (
        <p
          className="line-clamp-2 text-xs text-muted-foreground"
          title={caption}
        >
          {caption}
        </p>
      ) : null}
    </div>
  );
};

const videoThumbnailSource = (url: string): string =>
  `${url.split("#", 1)[0]}#t=0.001`;

const ThumbIcon = ({ media }: { media: EchoflicksSessionMedia }) => {
  if (isGalleryVideo(media)) return <Video className="size-4" />;
  if (isAudio(media.mime_type)) return <Music className="size-4" />;
  return <ImageIcon className="size-4" />;
};

// ---------------------------------------------------------------------------
// The title: the on-screen title card + why it was chosen
// ---------------------------------------------------------------------------

export const EchoTitleSection = ({
  envelope,
  media,
  loading,
}: {
  envelope: EnvelopeInput;
  media: EchoflicksSessionMedia[];
  loading?: boolean;
}) => {
  const scenes = useMemo(
    () => buildStoryboard(envelope, media),
    [envelope, media],
  );
  const titleScene = findTitleScene(scenes);
  const decision = summarizeTitleDecision(envelope);
  const rationale = decision?.rationale ?? null;

  if (!titleScene && !rationale) {
    return loading ? (
      <Card>
        <CardHeader>
          <CardTitle>The title</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyHint>Loading the title scene...</EmptyHint>
        </CardContent>
      </Card>
    ) : null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>The title</CardTitle>
        <p className="text-sm text-muted-foreground">
          The opening title card from the video — and why it was chosen.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
          {titleScene ? (
            <StoryboardThumb
              media={titleScene.media}
              titleText={titleScene.titleText}
              type={titleScene.type}
            />
          ) : null}
          <div className="min-w-0 space-y-3">
            {titleScene?.titleText ? (
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  On-screen title
                </div>
                <div className="text-lg font-semibold">
                  "{titleScene.titleText}"
                </div>
              </div>
            ) : null}
            {decision && decision.selected.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {decision.selected.map((card) => (
                  <Badge key={card} variant="outline" className="font-normal">
                    {card}
                  </Badge>
                ))}
              </div>
            ) : null}
            {rationale ? (
              <div className="flex items-start gap-2 rounded-md bg-muted/60 p-3 text-sm">
                <Quote className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 space-y-1">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Why this title
                  </div>
                  <p>{rationale}</p>
                </div>
              </div>
            ) : (
              <EmptyHint>
                No title rationale was recorded for this echo.
              </EmptyHint>
            )}
            {decision && decision.followedRecommendation === false ? (
              <p className="text-sm text-amber-700">
                Chose a card outside the recommendation
                {decision.preferred.length
                  ? ` — the suggested style was ${decision.preferred.join(", ")}.`
                  : "."}
              </p>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Director's decisions: the creative calls, in plain language
// ---------------------------------------------------------------------------

export const EchoDirectorDecisions = ({
  record,
  envelope,
  musicBpm,
  loading,
}: {
  record: EchoflicksVideoSession;
  envelope: EnvelopeInput;
  musicBpm?: number | null;
  loading?: boolean;
}) => {
  const script = directorScriptOf(envelope);
  const description = script?.description ?? record.script_description ?? null;

  const voice = summarizeVoice(script, record.is_narrated);
  const music = summarizeMusic(script);
  const style = typeof envelope?.style === "string" ? envelope.style : null;
  const lookValue = record.script_style ?? style;

  const chipSource: (Chip | null)[] = [
    lookValue
      ? { icon: <Palette className="size-3.5" />, label: lookValue }
      : null,
    script?.filter
      ? {
          icon: <Aperture className="size-3.5" />,
          label: `Filter: ${script.filter}`,
        }
      : null,
    music ? { icon: <Music className="size-3.5" />, label: music } : null,
    musicBpm
      ? {
          icon: <Gauge className="size-3.5" />,
          label: `ElevenLabs · ${musicBpm} BPM`,
          title:
            "Detected tempo of the ElevenLabs-generated music track (measured during music QC).",
        }
      : null,
    voice
      ? { icon: <Mic className="size-3.5" />, label: voice.label }
      : record.is_narrated
        ? { icon: <Mic className="size-3.5" />, label: "Narrated" }
        : null,
    script?.aspect_ratio
      ? {
          icon: <Type className="size-3.5" />,
          label: aspectLabel(script.aspect_ratio) ?? script.aspect_ratio,
        }
      : null,
  ];
  const chips = chipSource.filter((item): item is Chip => Boolean(item));

  if (!description && chips.length === 0) {
    return loading ? (
      <Card>
        <CardHeader>
          <CardTitle>Director's decisions</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyHint>Loading the director's choices...</EmptyHint>
        </CardContent>
      </Card>
    ) : null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Director's decisions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {description ? (
          <DecisionRow
            icon={<Sparkles className="size-4" />}
            label="Logline"
            heading={description}
          />
        ) : null}

        {chips.length > 0 ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {chips.map((chip) => (
              <Badge
                key={chip.label}
                variant="secondary"
                className="gap-1.5 font-normal"
                title={chip.title}
              >
                {chip.icon}
                {chip.label}
              </Badge>
            ))}
          </div>
        ) : loading ? (
          <EmptyHint>Loading the director's choices...</EmptyHint>
        ) : null}
      </CardContent>
    </Card>
  );
};

const DecisionRow = ({
  icon,
  label,
  heading,
  children,
}: {
  icon: ReactNode;
  label: string;
  heading: string;
  children?: ReactNode;
}) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
      {icon}
    </div>
    <div className="min-w-0 space-y-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="font-medium">{heading}</div>
      {children}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Storyboard: the prompts that went in, made readable
// ---------------------------------------------------------------------------

export const EchoStoryboard = ({
  envelope,
  media,
  sceneI2VByScene,
  loading,
}: {
  envelope: EnvelopeInput;
  media: EchoflicksSessionMedia[];
  sceneI2VByScene?: Map<string | number, SceneI2VAudit> | null;
  loading?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const scenes = useMemo(
    () => buildStoryboard(envelope, media, sceneI2VByScene),
    [envelope, media, sceneI2VByScene],
  );
  const cut = useMemo(() => summarizeCut(scenes, media), [scenes, media]);

  const visible = expanded ? scenes : scenes.slice(0, STORYBOARD_PREVIEW_COUNT);
  const hiddenCount = scenes.length - visible.length;

  return (
    <Card id="echo-storyboard">
      <CardHeader>
        <CardTitle>Storyboard</CardTitle>
        <p className="text-sm text-muted-foreground">
          Each shot, the prompt behind it, and the line spoken over it.
        </p>
      </CardHeader>
      <CardContent>
        {scenes.length > 0 ? (
          <div className="space-y-3">
            <EchoCutStats cut={cut} />
            {visible.map((scene) => (
              <div
                key={scene.key}
                className="grid gap-3 sm:grid-cols-[140px_minmax(0,1fr)]"
              >
                <StoryboardThumb
                  media={scene.media}
                  titleText={scene.titleText}
                  type={scene.type}
                />
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">
                      Scene {scene.index + 1}
                    </span>
                    <Badge variant="outline" className="font-normal">
                      {scene.kindLabel}
                    </Badge>
                    {scene.durationSeconds ? (
                      <span className="text-xs text-muted-foreground">
                        {formatSeconds(scene.durationSeconds)}
                      </span>
                    ) : null}
                  </div>
                  {scene.i2v ? <StaticVsI2VBadge scene={scene} /> : null}
                  {promptIsParallax(scene.prompt) ||
                  promptIsParallax(scene.i2v?.finalPositivePrompt) ? (
                    <Badge className="gap-1">
                      <Layers className="size-3" /> Parallax
                      {(() => {
                        const label =
                          parallaxMoveLabelFromPrompt(scene.prompt) ??
                          parallaxMoveLabelFromPrompt(
                            scene.i2v?.finalPositivePrompt,
                          );
                        return label ? ` · ${label}` : "";
                      })()}
                    </Badge>
                  ) : null}
                  {scene.prompt ? (
                    <p className="text-sm">
                      {scene.i2v?.finalPositivePrompt ? (
                        <span className="mr-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Planned:
                        </span>
                      ) : null}
                      {scene.prompt}
                    </p>
                  ) : scene.titleText ? (
                    <p className="text-sm font-medium">"{scene.titleText}"</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No prompt recorded for this shot.
                    </p>
                  )}
                  <I2VPromptBlock i2v={scene.i2v} />
                  {scene.narration.map((line, lineIndex) => (
                    <div
                      key={lineIndex}
                      className="flex items-start gap-1.5 text-sm text-muted-foreground"
                    >
                      <Mic className="mt-0.5 size-3.5 shrink-0" />
                      <span className="min-w-0 italic">
                        "{line.text}"
                        {line.spanEndIndex > line.spanStartIndex ? (
                          <span className="ml-1.5 whitespace-nowrap not-italic text-xs">
                            · plays over scenes {line.spanStartIndex + 1}–
                            {line.spanEndIndex + 1}
                          </span>
                        ) : null}
                        {line.sceneBound ? (
                          <span className="ml-1.5 whitespace-nowrap not-italic text-xs text-emerald-500">
                            · scene-bound
                            {typeof line.shiftSeconds === "number" &&
                            Math.abs(line.shiftSeconds) > 0.01
                              ? ` (${line.shiftSeconds > 0 ? "+" : ""}${line.shiftSeconds.toFixed(1)}s)`
                              : ""}
                          </span>
                        ) : null}
                        {line.rebound ? (
                          <span className="ml-1.5 whitespace-nowrap not-italic text-xs text-amber-500">
                            · re-bound by subject QC
                          </span>
                        ) : null}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {hiddenCount > 0 ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setExpanded(true)}
              >
                Show {hiddenCount} more shot{hiddenCount === 1 ? "" : "s"}
              </Button>
            ) : null}
          </div>
        ) : loading ? (
          <EmptyHint>Loading the storyboard...</EmptyHint>
        ) : (
          <EmptyHint>No scene plan is available for this echo.</EmptyHint>
        )}
      </CardContent>
    </Card>
  );
};

// The number of characters past which we collapse the effective prompt behind
// a "Show more" toggle (effective prompts run ~1–2 KB).
const I2V_PROMPT_COLLAPSE_CHARS = 320;

// The effective I2V prompt actually sent to the provider, next to the planned
// (script) prompt. Renders nothing for non-i2v scenes (title cards, static Ken
// Burns, raw video) where `i2v` is null.
const I2VPromptBlock = ({ i2v }: { i2v?: SceneI2VAudit | null }) => {
  const [expanded, setExpanded] = useState(false);
  if (!i2v) return null;

  const prompt = i2v.finalPositivePrompt?.trim();
  if (!prompt) {
    return (
      <p className="text-xs text-muted-foreground">
        I2V prompt not captured (summary only).
      </p>
    );
  }

  const collapsible = prompt.length > I2V_PROMPT_COLLAPSE_CHARS;
  const shown =
    collapsible && !expanded
      ? `${prompt.slice(0, I2V_PROMPT_COLLAPSE_CHARS).trimEnd()}…`
      : prompt;

  return (
    <div className="space-y-1 rounded-md border bg-muted/40 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="font-medium">
          I2V prompt
        </Badge>
        {i2v.provider ? (
          <Badge variant="outline" className="font-normal">
            {i2v.provider}
          </Badge>
        ) : null}
        {i2v.fallbackApplied ? (
          <Badge variant="destructive" className="font-normal">
            fallback: {i2v.fallbackFrom ?? "?"}→{i2v.fallbackTo ?? "?"}
          </Badge>
        ) : null}
        {i2v.raiFiltered ? (
          <Badge variant="destructive" className="font-normal">
            Safeguards
          </Badge>
        ) : null}
        {i2v.attemptCount && i2v.attemptCount > 1 ? (
          <Badge variant="outline" className="font-normal">
            {i2v.attemptCount} attempts
          </Badge>
        ) : null}
        <CopyButton value={prompt} label="Copy I2V prompt" />
      </div>
      <p className="whitespace-pre-wrap text-sm">{shown}</p>
      {collapsible ? (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      ) : null}
      {i2v.negativePrompt ? (
        <p className="text-xs text-muted-foreground">
          <span className="uppercase">Negative:</span> {i2v.negativePrompt}
        </p>
      ) : null}
    </div>
  );
};

// Surfaces the exact mislabel that hid the floating-head incident: an animated
// scene whose declared script type disagrees with the i2v's final scene type.
const StaticVsI2VBadge = ({ scene }: { scene: StoryScene }) => {
  const i2v = scene.i2v;
  if (
    !i2v?.animated ||
    !i2v.finalSceneType ||
    i2v.finalSceneType === scene.type
  ) {
    return null;
  }
  return (
    <Badge
      variant="outline"
      className="border-amber-300 font-normal text-amber-700"
    >
      Animated (declared {scene.type ?? "unknown"})
    </Badge>
  );
};

// ---------------------------------------------------------------------------
// I2V: the before/after of every animated shot. For each image-to-video scene
// it puts the source photo (fed to Kling / Veo) next to the clip it became, with
// the effective prompt, negative prompt, model and provider. Everything is signed
// on demand via useMediaUrl (private buckets, 1h TTL) — never a public URL.
// Pure join logic lives in i2vShowcase.ts (buildI2VPairs).
// ---------------------------------------------------------------------------

const I2V_SHOWCASE_PREVIEW_COUNT = 4;

export const EchoI2VBeforeAfter = ({
  envelope,
  media,
  sceneI2VByScene,
  loading,
}: {
  envelope: EnvelopeInput;
  media: EchoflicksSessionMedia[];
  sceneI2VByScene?: Map<string | number, SceneI2VAudit> | null;
  loading?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const pairs = useMemo(
    () => buildI2VPairs(envelope, media, sceneI2VByScene),
    [envelope, media, sceneI2VByScene],
  );

  if (pairs.length === 0) {
    // Nothing to show for title-only / static / pre-feature echoes: hide the
    // section entirely (only a hint while the script/manifest is still loading).
    return loading ? (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Film className="size-4" />
            I2V
          </CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyHint>Loading animated shots…</EmptyHint>
        </CardContent>
      </Card>
    ) : null;
  }

  const visible = expanded ? pairs : pairs.slice(0, I2V_SHOWCASE_PREVIEW_COUNT);
  const hiddenCount = pairs.length - visible.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Film className="size-4" />
          I2V
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Each animated shot — the source photo, the clip it became, and the
          prompt behind it.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {visible.map((pair) => (
            <I2VPairRow key={pair.key} pair={pair} />
          ))}
          {hiddenCount > 0 ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setExpanded(true)}
            >
              Show {hiddenCount} more animated shot
              {hiddenCount === 1 ? "" : "s"}
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

const I2VPairRow = ({ pair }: { pair: I2VPair }) => {
  const outputUrl = useMediaUrl(pair.outputMedia);
  const audit = pair.audit ?? null;
  const summary = pair.outputMedia.i2v_prompt_audit_summary ?? null;
  const provider =
    audit?.concreteProvider ??
    audit?.provider ??
    pair.outputMedia.concrete_provider ??
    pair.outputMedia.provider ??
    summary?.concrete_provider ??
    summary?.provider ??
    null;
  const model =
    audit?.model ?? pair.outputMedia.model ?? summary?.model ?? null;
  const sceneLabel =
    pair.sceneId != null ? `Scene ${pair.sceneId}` : "Animated shot";

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{sceneLabel}</span>
        <Badge variant="outline" className="font-normal">
          {/* Every pair here IS an animated shot (output-driven), so a secondary-route
              StaticImageScene must read as motion, not "Still photo". */}
          {sceneKindLabel(pair.sceneType, true)}
        </Badge>
        {pair.durationSeconds ? (
          <span className="text-xs text-muted-foreground">
            {formatSeconds(pair.durationSeconds)}
          </span>
        ) : null}
        {provider ? (
          <Badge variant="secondary" className="font-normal">
            {provider}
          </Badge>
        ) : null}
        {model ? (
          <Badge
            variant="outline"
            className="font-mono text-[11px] font-normal"
          >
            {model}
          </Badge>
        ) : null}
        {audit?.attemptCount && audit.attemptCount > 1 ? (
          <Badge variant="outline" className="font-normal">
            {audit.attemptCount} attempts
          </Badge>
        ) : null}
        {audit?.fallbackApplied ? (
          <Badge variant="destructive" className="font-normal">
            fallback: {audit.fallbackFrom ?? "?"}→{audit.fallbackTo ?? "?"}
          </Badge>
        ) : null}
        {audit?.raiFiltered ? (
          <Badge variant="destructive" className="font-normal">
            Safeguards
          </Badge>
        ) : null}
      </div>

      <div className="grid items-start gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <figure className="space-y-1">
          <figcaption className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Source
          </figcaption>
          {pair.sourceMedia ? (
            <StoryboardThumb
              media={pair.sourceMedia}
              titleText={null}
              type={pair.sceneType ?? null}
            />
          ) : (
            <div className="flex aspect-video w-full flex-col items-center justify-center gap-1 rounded-md bg-muted text-muted-foreground">
              <ImageIcon className="size-5" />
              <span className="px-2 text-center text-[11px]">
                {pair.sourceFile ?? "Source photo not linked"}
              </span>
            </div>
          )}
        </figure>

        <ArrowRight className="mt-8 hidden size-5 shrink-0 text-muted-foreground sm:block" />

        <figure className="space-y-1">
          <figcaption className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Animated
          </figcaption>
          {outputUrl.url ? (
            <video
              src={outputUrl.url}
              controls
              onError={outputUrl.refresh}
              className="aspect-video w-full rounded-md bg-black object-contain"
            />
          ) : outputUrl.isLoading ? (
            <div className="flex aspect-video w-full items-center justify-center rounded-md bg-black text-xs text-white/70">
              Preparing a private signed URL…
            </div>
          ) : (
            <div className="flex aspect-video w-full items-center justify-center rounded-md bg-muted text-muted-foreground">
              <Video className="size-5" />
            </div>
          )}
        </figure>
      </div>

      <I2VReferenceFrames pair={pair} />

      <I2VFrameStrip videoUrl={outputUrl.url} />

      <I2VRecipe audit={audit} summary={summary} />
    </div>
  );
};

// The true i2v REFERENCE frames: the uploaded images the model was asked to
// animate *between* — the start photo and, when one is used, the end-frame pin
// (Kling image_tail / Veo last_frame). This is what "first frame / last frame for
// i2v reference" means, and is deliberately distinct from the rendered clip's
// sampled endpoints below. Data comes from the composer manifest
// (`scenes[].i2v.reference_frames`); the section hides for older echoes that
// predate the capture. Most B2C shots are single-image (no end frame).
const I2VReferenceFrames = ({ pair }: { pair: I2VPair }) => {
  const ref = pair.audit?.referenceFrames ?? null;
  if (!ref) return null;

  const firstMedia = pair.firstFrameMedia ?? pair.sourceMedia;
  const endRole = ref.endFrame?.role ?? null;
  const endLabel =
    endRole === "match_cut_target"
      ? "Last frame · next shot (match-cut)"
      : endRole === "source_loopback"
        ? "Last frame · source looped back"
        : "Last frame";
  const referenceCount = ref.referenceFiles?.length ?? 0;

  return (
    <div className="space-y-1">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        I2V reference frames
      </div>
      <div className="grid grid-cols-2 gap-2">
        <ReferenceFrameFigure
          label="First frame"
          media={firstMedia}
          fileName={ref.firstFrame?.fileName ?? pair.sourceFile ?? null}
          type={pair.sceneType ?? null}
        />
        {ref.hasEndFrame ? (
          <ReferenceFrameFigure
            label={endLabel}
            media={pair.endFrameMedia}
            fileName={ref.endFrame?.fileName ?? null}
            type={pair.sceneType ?? null}
          />
        ) : (
          <figure className="space-y-1">
            <figcaption className="text-[11px] text-muted-foreground">
              Last frame
            </figcaption>
            <div className="flex aspect-video w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed bg-muted/40 px-2 text-center text-[11px] text-muted-foreground">
              None — single source image
            </div>
          </figure>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">
        The uploaded images the model animated between — the start photo and,
        when used, the end-frame pin. Most shots are single-image (no end
        frame).
        {referenceCount > 1
          ? ` This aerial shot used ${referenceCount} reference images.`
          : ""}
      </p>
    </div>
  );
};

const ReferenceFrameFigure = ({
  label,
  media,
  fileName,
  type,
}: {
  label: string;
  media?: EchoflicksSessionMedia;
  fileName: string | null;
  type: string | null;
}) => (
  <figure className="space-y-1">
    <figcaption className="text-[11px] text-muted-foreground">
      {label}
    </figcaption>
    {media ? (
      <StoryboardThumb media={media} titleText={null} type={type} />
    ) : (
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-1 rounded-md bg-muted text-muted-foreground">
        <ImageIcon className="size-4" />
        <span className="px-2 text-center text-[11px]">
          {fileName ?? "Not linked"}
        </span>
      </div>
    )}
  </figure>
);

// Extract the first and last RENDERED frames of an i2v clip in the browser, so a
// reviewer can see the clip's endpoints. When the two look identical the clip
// barely moved — the tell-tale of the B2C source-as-end-frame loopback (the
// composer pins the end frame back to the source photo to preserve the original
// expression, which Veo interpolates as near-static). This is a MOTION check on
// the rendered output — distinct from the I2V reference frames above (the uploaded
// inputs).
//
// Frames are drawn from the *already-signed* private URL onto a canvas entirely
// in-browser (never uploaded, never made public). It needs the media bucket's
// CORS to allow the CRM origin (it does) so the cross-origin <video> can be read
// back without tainting the canvas; if anything fails (CORS, decode) the strip
// simply hides — playback is unaffected because it uses a separate element.

type EndpointFrames = {
  status: "idle" | "loading" | "ready" | "unavailable";
  firstFrame: string | null;
  lastFrame: string | null;
};

function useVideoEndpointFrames(
  url: string | null | undefined,
): EndpointFrames {
  const [state, setState] = useState<EndpointFrames>({
    status: url ? "loading" : "idle",
    firstFrame: null,
    lastFrame: null,
  });

  useEffect(() => {
    if (!url) {
      setState({ status: "idle", firstFrame: null, lastFrame: null });
      return;
    }

    let active = true;
    setState({ status: "loading", firstFrame: null, lastFrame: null });

    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "auto";
    video.playsInline = true;
    const canvas = document.createElement("canvas");

    const capture = (): string | null => {
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return null;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.drawImage(video, 0, 0, w, h);
        // Throws a SecurityError if the canvas is tainted (CORS missing).
        return canvas.toDataURL("image/jpeg", 0.7);
      } catch {
        return null;
      }
    };

    const seekTo = (time: number) =>
      new Promise<void>((resolve) => {
        const onSeeked = () => {
          video.removeEventListener("seeked", onSeeked);
          resolve();
        };
        video.addEventListener("seeked", onSeeked);
        video.currentTime = time;
      });

    const run = async () => {
      await new Promise<void>((resolve, reject) => {
        video.addEventListener("loadeddata", () => resolve(), { once: true });
        video.addEventListener(
          "error",
          () => reject(new Error("video load error")),
          { once: true },
        );
      });
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      await seekTo(duration > 0.2 ? Math.min(0.04, duration / 4) : 0);
      const first = capture();
      await seekTo(duration > 0.2 ? Math.max(0, duration - 0.05) : duration);
      const last = capture();
      if (!active) return;
      setState(
        first || last
          ? { status: "ready", firstFrame: first, lastFrame: last }
          : { status: "unavailable", firstFrame: null, lastFrame: null },
      );
    };

    video.src = url;
    run().catch(() => {
      if (active) {
        setState({ status: "unavailable", firstFrame: null, lastFrame: null });
      }
    });

    return () => {
      active = false;
      video.removeAttribute("src");
      video.load();
    };
  }, [url]);

  return state;
}

const I2VFrameStrip = ({ videoUrl }: { videoUrl?: string | null }) => {
  const frames = useVideoEndpointFrames(videoUrl);
  if (frames.status !== "ready" || (!frames.firstFrame && !frames.lastFrame)) {
    return null;
  }
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Rendered motion check
      </div>
      <div className="grid grid-cols-2 gap-2">
        <FrameThumb label="Output start" src={frames.firstFrame} />
        <FrameThumb label="Output end" src={frames.lastFrame} />
      </div>
      <p className="text-[11px] text-muted-foreground">
        Sampled from the rendered clip (not the reference frames above). If the
        two look identical the shot barely moved — e.g. a B2C face scene that
        pinned the source photo back as its end frame to preserve the original
        expression.
      </p>
    </div>
  );
};

const FrameThumb = ({ label, src }: { label: string; src: string | null }) => (
  <figure className="space-y-1">
    <figcaption className="text-[11px] text-muted-foreground">
      {label}
    </figcaption>
    {src ? (
      <div className="relative">
        <img
          src={src}
          alt={label}
          className="aspect-video w-full rounded-md object-cover"
          loading="lazy"
        />
        <DisplayCropBadge />
      </div>
    ) : (
      <div className="flex aspect-video w-full items-center justify-center rounded-md bg-muted text-muted-foreground">
        <ImageIcon className="size-4" />
      </div>
    )}
  </figure>
);

// The prompt "recipe" for one animated shot: the effective positive prompt
// (collapsible + copy), the negative prompt, and — behind a toggle — the scene
// and source-preservation prompt breakdown. Falls back to a "summary only" note
// (with the prompt hash) when the full text was not captured in the manifest.
const I2VRecipe = ({
  audit,
  summary,
}: {
  audit: SceneI2VAudit | null;
  summary: I2VPromptAuditSummary | null;
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const positive = audit?.finalPositivePrompt?.trim() || null;
  const negative = audit?.negativePrompt?.trim() || null;
  const scenePrompt = audit?.scenePrompt?.trim() || null;
  const preservation = audit?.sourcePreservationPrompt?.trim() || null;

  if (!positive && !negative && !summary) return null;

  return (
    <div className="space-y-2 rounded-md border bg-muted/40 p-2">
      {positive ? (
        <CollapsiblePrompt title="Prompt" value={positive} />
      ) : (
        <p className="text-xs text-muted-foreground">
          Effective prompt not captured (summary only
          {summary?.positive_prompt_sha256
            ? ` · ${summary.positive_prompt_sha256.slice(0, 12)}`
            : ""}
          ).
        </p>
      )}
      {negative ? (
        <p className="text-xs text-muted-foreground">
          <span className="uppercase">Negative:</span> {negative}
        </p>
      ) : null}
      {scenePrompt || preservation ? (
        <>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => setShowDetails((prev) => !prev)}
          >
            {showDetails ? "Hide" : "Show"} prompt breakdown
          </Button>
          {showDetails ? (
            <div className="space-y-1.5">
              {scenePrompt ? (
                <PromptLine label="Scene motion" value={scenePrompt} />
              ) : null}
              {preservation ? (
                <PromptLine label="Source preservation" value={preservation} />
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const CollapsiblePrompt = ({
  title,
  value,
}: {
  title: string;
  value: string;
}) => {
  const [expanded, setExpanded] = useState(false);
  const collapsible = value.length > I2V_PROMPT_COLLAPSE_CHARS;
  const shown =
    collapsible && !expanded
      ? `${value.slice(0, I2V_PROMPT_COLLAPSE_CHARS).trimEnd()}…`
      : value;
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="font-medium">
          {title}
        </Badge>
        <CopyButton value={value} label={`Copy ${title.toLowerCase()}`} />
      </div>
      <p className="whitespace-pre-wrap text-sm">{shown}</p>
      {collapsible ? (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      ) : null}
    </div>
  );
};

const PromptLine = ({ label, value }: { label: string; value: string }) => (
  <div className="text-xs">
    <span className="font-medium text-muted-foreground">{label}: </span>
    <span className="whitespace-pre-wrap">{value}</span>
  </div>
);

const CopyButton = ({ value, label }: { value: string; label?: string }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard.writeText(value).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => setCopied(false),
    );
  };
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-6 gap-1 px-1.5 text-xs"
      onClick={onCopy}
      aria-label={label ?? "Copy"}
      title={label ?? "Copy"}
    >
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
};

const EchoCutStats = ({ cut }: { cut: EchoCutSummary }) => {
  const stats: { label: string; value: string }[] = [];
  if (cut.photosUsed != null) {
    stats.push({
      label: "Photos used",
      value: `${cut.photosUsed} of ${cut.photosSent}`,
    });
  } else if (cut.photosSent > 0) {
    stats.push({ label: "Photos", value: `${cut.photosSent}` });
  }
  stats.push({ label: "Shots", value: `${cut.shots}` });
  if (cut.runtimeSeconds) {
    stats.push({ label: "Runtime", value: formatSeconds(cut.runtimeSeconds) });
  }
  stats.push({
    label: "Narration",
    value: cut.narrationLines
      ? `${cut.narrationLines} line${cut.narrationLines === 1 ? "" : "s"}`
      : "Silent",
  });

  return (
    <div className="grid grid-cols-2 gap-2 border-b pb-3 sm:grid-cols-4">
      {stats.map((stat) => (
        <div key={stat.label} className="rounded-md bg-muted/50 px-3 py-2">
          <div className="text-xs text-muted-foreground">{stat.label}</div>
          <div className="text-sm font-medium">{stat.value}</div>
        </div>
      ))}
    </div>
  );
};

const StoryboardThumb = ({
  media,
  titleText,
  type,
}: {
  media?: EchoflicksSessionMedia;
  titleText: string | null;
  type: string | null;
}) => {
  const mediaUrl = useDisplayableImage(media);

  if (mediaUrl.url && isImage(media?.mime_type)) {
    return (
      <div className="relative">
        <img
          src={mediaUrl.url}
          alt={media?.s3_key ?? "Scene source"}
          className="aspect-video w-full rounded-md object-cover"
          loading="lazy"
          onError={mediaUrl.refresh}
        />
        <DisplayCropBadge />
      </div>
    );
  }

  if (mediaUrl.url && isVideo(media?.mime_type)) {
    return (
      <video
        src={mediaUrl.url}
        className="aspect-video w-full rounded-md bg-black object-contain"
        controls
        onError={mediaUrl.refresh}
      />
    );
  }

  if (type === "TitleCardScene" && titleText) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-md bg-primary p-2 text-center text-xs font-semibold text-primary-foreground">
        {titleText}
      </div>
    );
  }

  return (
    <div className="flex aspect-video w-full items-center justify-center rounded-md bg-muted text-muted-foreground">
      {type === "VideoScene" ? (
        <Video className="size-5" />
      ) : (
        <ImageIcon className="size-5" />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Quality checks: what our QC agents flagged. Observe-only — a "flagged"
// verdict means QC caught issues but did NOT block delivery.
// ---------------------------------------------------------------------------

export const EchoQualityChecks = ({
  session,
}: {
  session: EchoflicksVideoSession;
}) => {
  const qc = useMemo(() => summarizeQc(session), [session]);
  const reportSource =
    session.qc_report_storage_bucket && session.qc_report_storage_path
      ? {
          storage_bucket: session.qc_report_storage_bucket,
          storage_path: session.qc_report_storage_path,
          storage_is_public: false,
        }
      : null;

  if (!qc) return null; // hide until the backend projects a QC verdict

  const observeOnly = qc.ran && !qc.blocked && qc.totalIssues > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Quality checks</CardTitle>
          <QcVerdictBadge qc={qc} />
        </div>
        <p className="text-sm text-muted-foreground">
          Automated checks our director runs on every echo.
          {observeOnly
            ? " Observe-only — these were flagged but did not block delivery."
            : null}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {qc.verdict === "skipped" ? (
          <EmptyHint>Quality checks weren't run for this echo.</EmptyHint>
        ) : qc.totalIssues === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="size-4 text-emerald-600" />
            All checks passed — nothing flagged.
          </div>
        ) : (
          <>
            <div className="text-sm text-muted-foreground">
              {[
                qc.critical ? `${qc.critical} critical` : null,
                qc.warning
                  ? `${qc.warning} warning${qc.warning === 1 ? "" : "s"}`
                  : null,
                qc.info ? `${qc.info} info` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
            <div className="divide-y overflow-hidden rounded-md border">
              {qc.findings.map((finding, index) => (
                <QcFindingRow
                  key={`${finding.code}-${index}`}
                  finding={finding}
                />
              ))}
            </div>
          </>
        )}
        {reportSource ? <QcReportLink source={reportSource} /> : null}
      </CardContent>
    </Card>
  );
};

const QcVerdictBadge = ({ qc }: { qc: QcSummary }) => {
  const label = qcVerdictLabel(qc.verdict);
  if (qc.verdict === "passed" || qc.verdict === "repaired") {
    return (
      <Badge variant="secondary" className="gap-1 font-normal">
        <ShieldCheck className="size-3.5" />
        {label}
      </Badge>
    );
  }
  if (qc.verdict === "flagged") {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-amber-300 font-normal text-amber-700"
      >
        <ShieldAlert className="size-3.5" />
        {label}
        {qc.blocked ? " · blocked" : " · didn't block"}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="font-normal text-muted-foreground">
      {label}
    </Badge>
  );
};

const SeverityDot = ({ severity }: { severity: string }) => {
  const s = severity.toLowerCase();
  const cls =
    s === "critical"
      ? "bg-destructive"
      : s === "warning"
        ? "bg-amber-500"
        : "bg-muted-foreground/50";
  return (
    <span
      className={`mt-1.5 size-2 shrink-0 rounded-full ${cls}`}
      aria-label={severity}
    />
  );
};

const QcFindingRow = ({ finding }: { finding: EchoQcFinding }) => (
  <div className="flex items-start gap-2.5 p-3 text-sm">
    <SeverityDot severity={finding.severity} />
    <div className="min-w-0">
      <p className="min-w-0">{finding.message}</p>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span>{qcAgentLabel(finding.layer_name)}</span>
        {finding.scene_id != null ? (
          <span>· Scene {finding.scene_id}</span>
        ) : null}
        {typeof finding.confidence === "number" ? (
          <span>· {Math.round(finding.confidence * 100)}% confident</span>
        ) : null}
      </div>
    </div>
  </div>
);

const QcReportLink = ({
  source,
}: {
  source: {
    storage_bucket: string;
    storage_path: string;
    storage_is_public: boolean;
  };
}) => {
  const url = useMediaUrl(source);
  if (!url.url) return null;
  return (
    <Button asChild variant="link" size="sm" className="h-auto p-0">
      <a href={url.url} target="_blank" rel="noreferrer">
        Open full QC report (JSON)
        <ExternalLink className="size-3" />
      </a>
    </Button>
  );
};

// Per-layer QC status grid — Debug view only.
export const EchoQcLayers = ({
  session,
}: {
  session: EchoflicksVideoSession;
}) => {
  const qc = useMemo(() => summarizeQc(session), [session]);
  if (!qc || qc.layers.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Quality checks — per layer</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {qc.layers.map((layer) => (
            <div
              key={layer.layer_name}
              className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
            >
              <span className="min-w-0 truncate">
                {qcAgentLabel(layer.layer_name)}
              </span>
              <Badge
                variant={
                  layer.status === "failed" || layer.status === "error"
                    ? "destructive"
                    : layer.status === "warning"
                      ? "secondary"
                      : "outline"
                }
                className="font-normal"
              >
                {layer.status}
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
